import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useReferentialsData } from '@/hooks/useReferentialsData';
import type {
  AutocompleteField as AutocompleteFieldType,
  CheckboxField as CheckboxFieldType,
  DateField as DateFieldType,
  FileUploaderField as FileUploaderFieldType,
  ReportFormData,
  FormField,
  FormFieldValue,
  FormSection,
  LocationField as LocationFieldType,
  NumericField as NumericFieldType,
  SelectField as SelectFieldType,
  SpeciesSearchField as SpeciesSearchFieldType,
  TextAreaField as TextAreaFieldType,
  ToggleGroupField as ToggleGroupFieldType,
} from '@/screens/ReportCreation/reportCreation.types';
import { isFieldVisible } from '@/screens/ReportCreation/reportCreation.utils';
import {
  buildDependencyValueMap,
  DependencyValue,
  getConditionalDependencyNames,
} from '@/screens/ReportCreation/utils/conditionalVisibility.utils';
import { getFieldValidationRules } from '@/screens/ReportCreation/utils/fieldValidation.utils';
import { resolveDynamicOptions } from '@/screens/ReportCreation/utils/formFieldOptions.utils';
import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import type { FieldPath } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import { SizableText, YStack } from 'tamagui';
import { ControlledAutocompleteField } from './fields/AutocompleteField/ControlledAutocompleteField';
import { ControlledCheckboxField } from './fields/CheckBoxField/ControlledCheckboxField';
import { ControlledDateField } from './fields/DateField/ControlledDateField';
import { ControlledFileUploaderField } from './fields/FileUploaderField/ControlledFileUploaderField';
import type {
  ControlledFieldController,
  ControllerRules,
} from './fields/formController.types';
import { ControlledLocationField } from './fields/LocationField/ControlledLocationField';
import { ControlledNumericField } from './fields/NumericField/ControlledNumericField';
import { ControlledSelectField } from './fields/SelectField/ControlledSelectField';
import { ControlledSpeciesSearchField } from './fields/SpeciesSearchField/ControlledSpeciesSearchField';
import { ControlledTextAreaField } from './fields/TextAreaField/ControlledTextAreaField';
import { ControlledToggleGroupField } from './fields/ToggleGroupField/ControlledToggleGroupField';
import { SectionWarning } from './SectionWarning';

interface FormSectionListProps {
  sections?: FormSection[];
}

/**
 * Renders a list of form sections with the appropriate field controllers.
 * Handles all conditional visibility logic so higher-level components only
 * provide declarative configuration.
 */
export const FormSectionList = ({
  sections = [],
}: FormSectionListProps): React.ReactElement | null => {
  const { t } = useAppTranslation();
  const requiredMessage = t('reportCreation.errors.requiredField');
  const { control, getValues, setValue } = useFormContext<ReportFormData>();
  const { data: referentials } = useReferentialsData();

  const wahisAgentCodes = useMemo(
    () =>
      referentials?.wahisEtiologicalAgents?.map((agent) => agent.code) ?? [],
    [referentials],
  );

  const dependencyNames = useMemo(
    () => getConditionalDependencyNames(sections),
    [sections],
  );

  const watchedDependencyValues = useWatch({
    control,
    name: dependencyNames,
  }) as DependencyValue[] | undefined;

  const dependencyValueMap = buildDependencyValueMap(
    dependencyNames,
    watchedDependencyValues ?? [],
  );

  const warningDependencyNames = useMemo(() => {
    const dependencies = sections.flatMap((section) =>
      section.fields.flatMap(
        (field) =>
          field.warnings?.flatMap((warning) => warning.dependsOn ?? []) ?? [],
      ),
    );

    return Array.from(new Set(dependencies));
  }, [sections]);

  const warningDependencyValues = useWatch({
    control,
    name: warningDependencyNames,
  });

  const warningValueMap = buildDependencyValueMap(
    warningDependencyNames,
    warningDependencyValues ?? [],
  );

  useEffect(() => {
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        const isSelectableField =
          field.type === 'select' || field.type === 'autocomplete-search';

        const isCustomAutocomplete =
          field.type === 'autocomplete-search' && field.allowCustomValue;

        if (
          !isSelectableField || // Only select-like inputs that need dynamic option resolution.
          !field.dynamicOptions || // No dynamic options means nothing to refresh for this field.
          isCustomAutocomplete // Custom autocomplete inputs should keep user-provided values.
        ) {
          return;
        }

        const options = resolveDynamicOptions(field, dependencyValueMap);
        const currentValue = getValues(field.id);

        if (
          typeof currentValue === 'string' &&
          currentValue.length > 0 &&
          !options.some((option) => option.value === currentValue)
        ) {
          setValue(field.id, '', {
            shouldValidate: true,
          });
        }
      });
    });
  }, [dependencyValueMap, getValues, sections, setValue]);

  const getFieldRules = (
    field: FormField,
  ): ControllerRules<ReportFormData> | undefined =>
    getFieldValidationRules({
      field,
      getValues,
      wahisAgentCodes,
    });

  const buildFormController = (
    field: FormField,
  ): ControlledFieldController<ReportFormData> => ({
    name: field.id,
    control,
    rules: getFieldRules(field),
  });

  const renderField = (field: FormField): React.ReactNode => {
    if (
      (field.type === 'select' || field.type === 'autocomplete-search') &&
      field.dynamicOptions &&
      resolveDynamicOptions(field, dependencyValueMap).length === 0 &&
      !(field.type === 'autocomplete-search' && field.allowCustomValue)
    ) {
      return null;
    }

    if (!isFieldVisible(field, dependencyValueMap)) {
      return null;
    }

    switch (field.type) {
      case 'toggle-group': {
        const toggleField = field as ToggleGroupFieldType;
        return (
          <ControlledToggleGroupField
            key={field.id}
            field={toggleField}
            formController={buildFormController(toggleField)}
          />
        );
      }

      case 'select': {
        const selectField = field as SelectFieldType;
        const resolvedField = {
          ...selectField,
          options: resolveDynamicOptions(selectField, dependencyValueMap),
        };

        return (
          <ControlledSelectField
            key={field.id}
            field={resolvedField}
            formController={buildFormController(resolvedField)}
          />
        );
      }

      case 'species-search': {
        const speciesField = field as SpeciesSearchFieldType;
        return (
          <ControlledSpeciesSearchField
            key={field.id}
            field={speciesField}
            formController={buildFormController(speciesField)}
          />
        );
      }

      case 'location': {
        const locationField = field as LocationFieldType;
        const latitudeName = `${locationField.id}.latitude`;
        const longitudeName = `${locationField.id}.longitude`;

        return (
          <ControlledLocationField
            key={field.id}
            field={locationField}
            formController={{
              control,
              latitudeName,
              longitudeName,
              rules: getFieldRules(locationField),
            }}
          />
        );
      }

      case 'checkbox': {
        const checkboxField = field as CheckboxFieldType;
        return (
          <ControlledCheckboxField
            key={field.id}
            field={checkboxField}
            errorMessage={requiredMessage}
          />
        );
      }

      case 'date': {
        const dateField = field as DateFieldType;
        return (
          <ControlledDateField
            key={field.id}
            label={dateField.label}
            placeholder={dateField.placeholder}
            required={dateField.required}
            formController={buildFormController(dateField)}
          />
        );
      }

      case 'numeric': {
        const numericField = field as NumericFieldType;
        return (
          <ControlledNumericField
            key={field.id}
            field={numericField}
            formController={buildFormController(numericField)}
          />
        );
      }

      case 'textarea': {
        const textareaField = field as TextAreaFieldType;
        return (
          <ControlledTextAreaField
            key={field.id}
            field={textareaField}
            formController={buildFormController(textareaField)}
          />
        );
      }

      case 'autocomplete-search': {
        const autocompleteField = field as AutocompleteFieldType;
        const resolvedField = {
          ...autocompleteField,
          options: resolveDynamicOptions(autocompleteField, dependencyValueMap),
        };
        return (
          <ControlledAutocompleteField
            key={field.id}
            field={resolvedField}
            formController={buildFormController(resolvedField)}
          />
        );
      }

      case 'file-uploader': {
        const fileField = field as FileUploaderFieldType;
        return (
          <ControlledFileUploaderField
            key={field.id}
            field={fileField}
            formController={buildFormController(fileField)}
          />
        );
      }

      default:
        return null;
    }
  };

  if (!sections.length) {
    return null;
  }

  const getWarningValue = (name: FieldPath<ReportFormData>): FormFieldValue => {
    if (Object.prototype.hasOwnProperty.call(warningValueMap, name)) {
      return warningValueMap[name];
    }

    return getValues(name);
  };

  const renderFieldWarnings = (field: FormField): ReactNode => {
    if (!field.warnings?.length) {
      return null;
    }

    const fieldWarnings = field.warnings
      .filter((warning) => warning.isVisible(getWarningValue))
      .map((warning, warningIndex) => (
        <SectionWarning
          key={`field-warning-${field.id}-${warning.id}-${warningIndex}`}
        >
          {warning.renderContent()}
        </SectionWarning>
      ));

    return fieldWarnings.length ? fieldWarnings : null;
  };

  const renderFieldBlock = (field: FormField): ReactNode => {
    const fieldNode = renderField(field);

    if (!fieldNode) {
      return null;
    }

    return (
      <YStack key={`field-${field.id}`} gap="$2">
        {fieldNode}
        {renderFieldWarnings(field)}
      </YStack>
    );
  };

  return (
    <YStack gap="$true">
      {sections.map((section, sectionIndex) => (
        <YStack key={sectionIndex} gap="$2">
          {section.title && (
            <SizableText size="$body" fontFamily="$bold" color="$color">
              {section.title}
            </SizableText>
          )}

          <YStack gap="$true">
            {section.fields.map((field) => renderFieldBlock(field))}
          </YStack>
        </YStack>
      ))}
    </YStack>
  );
};
